/**
 * Product-page Flash Offer.
 *
 * Companion to assets/cart-flash-offer.js. That file owns the offer inside the
 * cart drawer; this one owns it on the product page and, critically, is what
 * makes the discount actually apply when the shopper adds from that page.
 *
 * Shared state
 * ------------
 * The countdown deadline lives in localStorage under EXACTLY the same key the
 * drawer uses (`cn:flash:<code>:<minutes>`, scoped to the local date). That is
 * deliberate: the two views must show the same clock and expire together. If
 * this file ever drifts from the drawer's `todayKey`/`storeKey`/state shape,
 * the product page and the drawer will run two independent countdowns and the
 * shopper will be shown a price the cart then refuses.
 *
 * Applying the discount
 * ---------------------
 * Dawn's product-form.js publishes `cartUpdate` after a successful add. We
 * subscribe, and when the added variant is one of ours and the offer is still
 * live, POST the code to /cart/update.js and then force the drawer to re-read
 * the cart. `publish()` in pubsub.js returns Promise.all of its subscribers,
 * but product-form.js does NOT await it before calling renderContents() — so a
 * re-render is scheduled after a short settle rather than issued immediately,
 * otherwise Dawn repaints the drawer with the pre-discount payload and wins.
 *
 * PUB_SUB_EVENTS is a top-level `const` in constants.js, so it lives in the
 * global LEXICAL environment and is NOT a property of `window`; it must be
 * probed with `typeof`, never as `window.PUB_SUB_EVENTS`.
 */
(function () {
  if (window.ProductFlashOfferLoaded) return;
  window.ProductFlashOfferLoaded = true;

  var routes = window.routes || {};
  var UPDATE_URL = routes.cart_update_url || '/cart/update.js';

  var URGENT_MS = 60000;
  var RERENDER_SETTLE_MS = 450;

  function events() {
    return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  }

  /**
   * Update text WITHOUT producing a childList mutation.
   *
   * `el.textContent = x` REPLACES the child text node, so it counts as a
   * childList change. GoKwik re-enables its checkout button from a
   * MutationObserver on <body> that watches childList and is debounced by
   * 700ms — a once-a-second childList write from this countdown kept resetting
   * that debounce, so the button was never re-enabled and sat spinning forever.
   * Writing through an existing text node's nodeValue is a characterData
   * mutation, which that observer does not watch.
   */
  function writeText(el, text) {
    if (!el) return;
    var node = el.firstChild;
    if (!node || node.nodeType !== 3 || el.childNodes.length !== 1) {
      el.textContent = '';
      el.appendChild(document.createTextNode(text));
      return;
    }
    if (node.nodeValue !== text) node.nodeValue = text;
  }

  /* ---------------------------------------------------------------- *
   * Shared once-per-day state — keep in lockstep with cart-flash-offer.js
   * ---------------------------------------------------------------- */
  function todayKey() {
    var d = new Date();
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  function storeKey(code, minutes) {
    return 'cn:flash:' + code + ':' + minutes;
  }

  function readState(code, minutes) {
    try {
      var raw = JSON.parse(localStorage.getItem(storeKey(code, minutes)));
      if (!raw || typeof raw !== 'object') return null;
      if (raw.day !== todayKey()) return null;
      return raw;
    } catch (e) {
      return null;
    }
  }

  function writeState(code, minutes, state) {
    try {
      state.day = todayKey();
      localStorage.setItem(storeKey(code, minutes), JSON.stringify(state));
    } catch (e) {
      /* Private mode — degrades to per-page behaviour. */
    }
  }

  if (customElements.get('product-flash-offer')) return;

  customElements.define(
    'product-flash-offer',
    class ProductFlashOffer extends HTMLElement {
      connectedCallback() {
        this.code = this.getAttribute('data-code') || '';
        this.minutes = parseInt(this.getAttribute('data-minutes') || '5', 10) || 5;
        this.variantId = String(this.getAttribute('data-variant') || '');

        this.digitsEl = this.querySelector('[data-pfo-digits]');
        this.barEl = this.querySelector('[data-pfo-bar]');
        this.priceEl = this.querySelector('[data-pfo-price]');
        this.wasEl = this.querySelector('[data-pfo-was]');

        this.prices = {};
        var json = this.querySelector('[data-pfo-prices]');
        if (json) {
          try {
            this.prices = JSON.parse(json.textContent) || {};
          } catch (e) {
            this.prices = {};
          }
        }

        if (!this.code) return;

        this._boot();
      }

      disconnectedCallback() {
        if (this._interval) {
          clearInterval(this._interval);
          this._interval = null;
        }
        if (this._unsubCart) this._unsubCart();
        if (this._unsubVariant) this._unsubVariant();
      }

      /* -------------------------------------------------------------- *
       * Lifecycle
       * -------------------------------------------------------------- */
      _boot() {
        var state = readState(this.code, this.minutes);

        /* Burned for the day: show nothing and, crucially, do not hook the add
           button — the shopper must not be charged against a dead offer. */
        if (state && state.done) return;

        var deadline;
        if (state && state.deadline) {
          if (Date.now() >= state.deadline) {
            writeState(this.code, this.minutes, { deadline: 0, done: true });
            return;
          }
          deadline = state.deadline;
        } else {
          /* First sight of the offer today. Landing on a flash product's page
             is what starts the clock here, whereas in the drawer it is opening
             the drawer — either way the SAME deadline is written, so whichever
             the shopper reaches first governs both. */
          deadline = Date.now() + this.minutes * 60000;
          writeState(this.code, this.minutes, { deadline: deadline, done: false });
        }

        this.hidden = false;
        this._run(deadline);
        this._listen();
      }

      _run(deadline) {
        this._deadline = deadline;
        this._span = this.minutes * 60000;
        this._tick();
        this._interval = setInterval(this._tick.bind(this), 1000);
      }

      _tick() {
        var remaining = Math.max(0, this._deadline - Date.now());
        var mins = Math.floor(remaining / 60000);
        var secs = Math.floor((remaining % 60000) / 1000);

        writeText(
          this.digitsEl,
          (mins < 10 ? '0' + mins : mins) + ':' + (secs < 10 ? '0' + secs : secs),
        );
        if (this.barEl) {
          var width = (remaining / this._span) * 100 + '%';
          if (this.barEl.style.width !== width) this.barEl.style.width = width;
        }
        this.classList.toggle('pfo--urgent', remaining > 0 && remaining <= URGENT_MS);

        if (remaining <= 0) {
          clearInterval(this._interval);
          this._interval = null;
          this._expire();
        }
      }

      _expire() {
        writeState(this.code, this.minutes, { deadline: 0, done: true });
        this.hidden = true;
        if (this._unsubCart) {
          this._unsubCart();
          this._unsubCart = null;
        }
      }

      /** Is the offer still genuinely live right now? */
      _live() {
        if (this.hidden) return false;
        var state = readState(this.code, this.minutes);
        if (!state || state.done || !state.deadline) return false;
        return Date.now() < state.deadline;
      }

      /* -------------------------------------------------------------- *
       * Hooks
       * -------------------------------------------------------------- */
      _listen() {
        var self = this;
        var ev = events();
        var sub = typeof subscribe === 'function' ? subscribe : null;
        if (!ev || !sub) return;

        this._unsubCart = sub(ev.cartUpdate, function (data) {
          /* Only react to a genuine product-form add. Ignoring every other
             source also stops our own re-render publish from re-entering. */
          if (!data || data.source !== 'product-form') return;
          if (!self._live()) return;
          if (!self._isOurs(data.productVariantId)) return;
          return self._applyDiscount();
        });

        this._unsubVariant = sub(ev.variantChange, function (data) {
          var variant = data && data.variant;
          if (!variant) return;
          self._showVariant(String(variant.id));
        });
      }

      _isOurs(variantId) {
        if (!variantId) return false;
        return Object.prototype.hasOwnProperty.call(this.prices, String(variantId));
      }

      _showVariant(variantId) {
        this.variantId = variantId;
        var entry = this.prices[variantId];
        if (!entry) return;
        if (this.priceEl) this.priceEl.textContent = entry.flash;
        if (this.wasEl) this.wasEl.textContent = entry.was;
      }

      _applyDiscount() {
        var self = this;
        var form = new FormData();
        form.append('discount', this.code);

        return fetch(UPDATE_URL, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: form,
        })
          .then(function (response) {
            if (!response.ok) throw new Error('discount update failed');
            return response.json();
          })
          .then(function () {
            self._rerenderCart();
          })
          .catch(function () {
            /* Never block or break the add itself. The item is already in the
               cart; the shopper simply would not see the discount until the
               drawer next refreshes. */
          });
      }

      /**
       * Repaint the drawer from the server AFTER Dawn has finished its own
       * (pre-discount) render, so the discounted totals are what survive.
       */
      _rerenderCart() {
        setTimeout(function () {
          var items = document.querySelector('cart-drawer-items');
          if (items && typeof items.onCartUpdate === 'function') {
            items.onCartUpdate();
            return;
          }
          var ev = events();
          var send = typeof publish === 'function' ? publish : null;
          if (ev && send) send(ev.cartUpdate, { source: 'product-flash-offer' });
        }, RERENDER_SETTLE_MS);
      }
    },
  );
})();
